// @ts-strict

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NotificationTypes, NotificationType } from '@app/core/services/global-notifications/global-notifications.model';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UrlAddressConfigService } from '@services/url-address-config/url-address-config.service';
import { urlConfigInputTypes } from '@app/core/helpers/campaign-configs-input-types';
import { ICampaignConfigFieldData, ISingleCampaign, ICommonInputValue, ICheckboxValue, SpecialCampaignData } from '@app/core/models/core-models';
import { CreatedConfigurationResponse, EditedConfig, ICampaignFieldData } from '@app/core/services/url-address-config/url-address-config.types';
import { TextInputData } from '@app/shared/components/modal-inputs/text-input/text-input.component';
import { SelectValue } from '@app/shared/components/modal-inputs/select-input/select-input.component';
import { TextareaInputValueEmitter } from '@app/shared/components/modal-inputs/textarea-input/textarea-input.component';
import { IvrPpiModalContainerComponent } from '@app/shared/components/ivr-ppi-modal-container/ivr-ppi-modal-container.component';
import { CollectActivityService } from '@services/collect-activity/collect-activity.service';
import { ISelectOption } from '@app/shared/types/inputs';

@Component({
  selector: 'app-add-edit-config-modal',
  templateUrl: './add-edit-config-modal.component.html',
  styleUrls: ['./add-edit-config-modal.component.scss']
})
export class AddEditConfigModalComponent implements OnInit {
  @Input() configurationData?: ICampaignConfigFieldData[];
  @Input() landingOptionsData?: { id: number; name: string }[];
  @Input() currentCampaign?: ISingleCampaign | SpecialCampaignData;
  @Input() configURL: string = '';
  @Input() configID?: number;
  @Input() shortVersionWithoutSecondStep: boolean = false;
  @Output() emitAction: EventEmitter<{ action: string; data: CreatedConfigurationResponse | undefined }> = new EventEmitter();

  public submitted = false;
  public isSuccessfullySubmitted = false;
  public configurationName: string = '';
  public generatedURL: string = '';
  public modalConfigFields: ICampaignFieldData[] = [];
  public landingOptions: ISelectOption[] = [];
  public readonly inputTypes = urlConfigInputTypes;
  public isLoaded = false;
  public showEditButton = false;

  private fieldsToSend: Record<string, string | number | string[]> = {};
  private campaignId?: number;
  private selectedLandingParam?: string;
  private mlSubsArray?: string[];
  private originalURL: string = '';

  constructor(
    private globalNotificationsService: GlobalNotificationsService,
    public activeModal: NgbActiveModal,
    private urlAddressConfig: UrlAddressConfigService,
    private readonly modalService: NgbModal,
    private readonly collectActivity: CollectActivityService
  ) {}

  ngOnInit(): void {
    if (!this.campaignId) {
      this.campaignId = this.currentCampaign?.id;
    }
    this.urlAddressConfig.getModalConfig(this.campaignId || 0).subscribe(res => {
      this.modalConfigFields = res.data?.fields || [];
      const tempArray: ISelectOption[] = [];
      if (this.landingOptionsData) {
        this.landingOptionsData.forEach(item => {
          tempArray.push({ id: item.id, value: item.id, name: item.name });
        });
        this.landingOptions = tempArray;
      }
      this.isLoaded = true;
    });
    if (this.configURL) {
      this.generatedURL = this.configURL;
      this.originalURL = this.generatedURL;
      this.fieldsToSend.config_id = this.configID || '';
    }
  }

  public performAction(): void {
    if (this.isSuccessfullySubmitted) {
      this.closeModal();
    } else {
      this.verify();
    }
  }

  public getValue(event: ICommonInputValue | TextInputData | SelectValue | ICheckboxValue | TextareaInputValueEmitter): void {
    if (event.system) {
      this.fieldsToSend[event.system] = event.value;
      if (this.configurationData) {
        const dataToCompare = this.configurationData.find(config => config.system_name === event.system);
        if (dataToCompare?.config_values !== event.value) {
          this.showEditButton = true;
          this.isSuccessfullySubmitted = false;
        }
      }
    }
  }

  public handleAddDirectlinkModal = () => {
    const modalRef = this.modalService.open(IvrPpiModalContainerComponent, {
      size: 'me'
    });

    const component = modalRef.componentInstance as IvrPpiModalContainerComponent;
    component.modalTitle = 'view_translations.add_direct_link';
    component.modalType = 'create_direct_link';
    component.campaignData = { id: this.campaignId as any };
  };

  public getLanding(event: any): void {
    if (event.id) {
      this.selectedLandingParam = 'ld=' + event.id;
      let tempArray = [this.selectedLandingParam];
      if (this.mlSubsArray && this.mlSubsArray?.length > 0) tempArray = [...tempArray, ...this.mlSubsArray];
      this.generatedURL = this.originalURL + '?' + tempArray.join('&');
    } else {
      this.selectedLandingParam = undefined;
      if (this.mlSubsArray && this.mlSubsArray.length > 0) {
        this.generatedURL = this.originalURL + '?' + this.mlSubsArray.join('&');
      } else {
        this.generatedURL = this.originalURL;
      }
    }
  }

  public getSubs(arrayOfSubs: Array<string>): void {
    // remove empty values from array
    arrayOfSubs = arrayOfSubs.filter(el => el);
    this.mlSubsArray = arrayOfSubs;
    let tempArrayX = [...arrayOfSubs];
    if (this.selectedLandingParam) tempArrayX = [this.selectedLandingParam, ...tempArrayX];
    this.generatedURL = this.originalURL + '?' + tempArrayX.join('&');
  }

  private verify(): void {
    this.submitted = true;
    if (Object.keys(this.fieldsToSend).length === 0) {
      this.showNotification('view_translations.fill_requied_fileds', NotificationTypes[1]);
      return;
    }

    if (this.configurationData) {
      this.editConfig();
    } else {
      this.addConfig();
    }
  }

  private addConfig(): void {
    this.urlAddressConfig.addConfiguration(this.fieldsToSend, this.campaignId || 0).subscribe(
      resURLConfig => {
        if (resURLConfig.status === 'success') {
          this.collectActivity.checkMeThenSendFingerprint();
          this.showNotification('view_translations.added_successfully', NotificationTypes[0]);
          this.isSuccessfullySubmitted = true;
          this.emitAction.emit({ action: 'add_config', data: resURLConfig.data });
          const newConfig: Record<string, string | number> = { ...resURLConfig.data?.configuration };
          newConfig.currency = 'EUR';
          newConfig.income = 0;
          newConfig.views = 0;
          this.urlAddressConfig.addedEditedConfig.next(newConfig as EditedConfig);
          this.generatedURL = resURLConfig.data?.configuration?.url || '';
          this.originalURL = this.generatedURL;
          if (this.shortVersionWithoutSecondStep) {
            this.activeModal.close();
          }
        }
      },
      _err => {
        if (_err.status === 422) {
          this.showNotification('view_translations.fill_requied_fileds', NotificationTypes[1]);

          return;
        }

        this.showNotification('view_translations.something_went_wrong_try_again_later', NotificationTypes[1]);
      }
    );
  }

  private editConfig(): void {
    this.urlAddressConfig.editCurrentConfig(this.campaignId || 0, this.fieldsToSend).subscribe(
      res => {
        if (res.status === 'success') {
          this.showNotification('view_translations.edited_successfully', NotificationTypes[0]);
          this.isSuccessfullySubmitted = true;
          this.emitAction.emit({ action: 'edit_config', data: res.data });
        }
        if (res.data?.configuration) {
          // this.urlAddressConfig.addedEditedConfig.next(res.data.configuration);
          this.urlAddressConfig.addedEditedConfig.next({ ...res.data.configuration });
          this.generatedURL = res.data.configuration.url ?? '';
          this.originalURL = this.generatedURL;
          if (this.mlSubsArray) {
            this.getSubs(this.mlSubsArray);
          }
        }
      },
      err => {
        if (err.status === 422) {
          this.showNotification('view_translations.fill_requied_fileds', NotificationTypes[1]);

          return;
        }
        this.showNotification('view_translations.something_went_wrong_try_again_later', NotificationTypes[1]);
      }
    );
  }

  private showNotification(message: string, type: NotificationType): void {
    this.globalNotificationsService.show({
      message,
      type,
      translate: true,
      isPersistent: false,
      id: ''
    });
  }

  private closeModal(): void {
    this.urlAddressConfig.isUrlEditClosed = true;
    this.activeModal.close();
  }
}
